import PropTypes from 'prop-types';

import AdditionalItemsForm from './AdditionalItemsForm';
import SuffixInput from './SuffixInput';
import { DECKING_OPTIONS, JOIST_SIZES, JOIST_SPACING, JOIST_MAXSPAN_INCHES } from './constants';
import { CalculatorColumn, FormRow, FormColumn, Group, ResetButton, HeaderArea } from './styles';
import useCalculator from './useCalculator';
import InputError from '../../ComponentLibrary/input-elements/oc-input-error';
import Label from '../../ComponentLibrary/input-elements/oc-label';
import RadioGroup from '../../ComponentLibrary/input-elements/oc-radio-group';
import SelectDropdown from '../../ComponentLibrary/input-elements/oc-select-dropdown';
import Button from '../../ComponentLibrary/oc-button';
// eslint-disable-next-line import/no-cycle
import { BasicContent } from '../../OC/PageBuilder/BasicContent';

const LumberDeckingForm = ({ copy }) => {
  const {
    calculatorValues,
    joistOverspan,
    handleFormChange,
    resetForm,
    calculate,
    isFormValid,
    toFeetAndInches,
    cantileverOverLimit,
  } = useCalculator();
  const {
    joistSize,
    joistSpacing,
    lengthFeet,
    lengthInches,
    cantileverLengthFeet,
    cantileverLengthInches,
  } = calculatorValues;

  const ledgerBoardOptions = [
    { text: 'Yes', value: 'yes', onChange: handleFormChange, checked: calculatorValues.ledgerBoard === 'yes' },
    { text: 'No', value: 'no', onChange: handleFormChange, checked: calculatorValues.ledgerBoard === 'no' },
  ];

  const joistMaxSpan = toFeetAndInches(JOIST_MAXSPAN_INCHES[joistSize]?.[joistSpacing] || 0);

  return (
    <CalculatorColumn>
      <HeaderArea>
        <BasicContent prehead={copy.prehead} heading={copy.heading} bodyCopy={copy.bodyCopy} />
      </HeaderArea>
      <FormRow>
        <FormColumn>
          <Label forId="widthFeet" required text="Width (ft. in.)" helperText="Required" helperTextColor="#585858" />
          <Group>
            <SuffixInput
              required
              suffix="ft."
              name="widthFeet"
              onChange={handleFormChange}
              value={calculatorValues.widthFeet}
              placeholder="0"
            />
            <SuffixInput
              required
              suffix="in."
              name="widthInches"
              onChange={handleFormChange}
              value={calculatorValues.widthInches}
              placeholder="0"
            />
          </Group>
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn>
          <Label
            required
            valid={!joistOverspan}
            text="Distance to beam length (ft. in.)"
            helperText="Required"
            helperTextColor="#585858"
          />
          <Group>
            <SuffixInput
              required
              touched={lengthFeet !== null}
              valid={!joistOverspan}
              suffix="ft."
              name="lengthFeet"
              onChange={handleFormChange}
              value={lengthFeet}
              placeholder="0"
            />
            <SuffixInput
              required
              touched={lengthFeet !== null}
              valid={!joistOverspan}
              suffix="in."
              name="lengthInches"
              onChange={handleFormChange}
              value={lengthInches}
              placeholder="0"
            />
          </Group>
          {joistOverspan && (
            <InputError
              icon
              // eslint-disable-next-line max-len
              message={`Over-spanned - Maximum span for joist is ${joistMaxSpan} based on Joist Size of ${joistSize} and Joist Spacing of ${joistSpacing}"`}
            />
          )}
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn>
          <Label
            required
            text="Cantilever length (ft. in.)"
            helperText="Required"
            helperTextColor="#585858"
            tooltip="Maximum cantilever length of 2ft or 25% of back span, whichever is shorter."
          />
          <Group>
            <SuffixInput
              required
              touched={cantileverLengthFeet !== null}
              suffix="ft."
              name="cantileverLengthFeet"
              onChange={handleFormChange}
              value={cantileverLengthFeet}
              placeholder="0"
            />
            <SuffixInput
              required
              touched={cantileverLengthInches !== null}
              suffix="in."
              name="cantileverLengthInches"
              onChange={handleFormChange}
              value={cantileverLengthInches}
              placeholder="0"
            />
          </Group>
          {cantileverOverLimit && (
            <InputError
              icon
              // eslint-disable-next-line max-len
              message="Cantilevers cannot exceed 25% of backspan or 2’-0”, whichever is less"
            />
          )}
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn>
          <Label required text="Decking" helperText="Required" helperTextColor="#585858" />
          <SelectDropdown
            required
            name="decking"
            options={DECKING_OPTIONS}
            onChange={handleFormChange}
            value={calculatorValues.decking}
          />
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn>
          <Label required text="Joist Size" helperText="Required" helperTextColor="#585858" />
          <SelectDropdown
            required
            name="joistSize"
            options={JOIST_SIZES}
            onChange={handleFormChange}
            value={calculatorValues.joistSize}
          />
        </FormColumn>
        <FormColumn>
          <Label required text="Joist Spacing" helperText="Required" helperTextColor="#585858" />
          <SelectDropdown
            required
            name="joistSpacing"
            options={JOIST_SPACING}
            onChange={handleFormChange}
            value={calculatorValues.joistSpacing}
          />
        </FormColumn>
      </FormRow>
      <FormRow>
        <FormColumn>
          <RadioGroup
            forId="ledgerBoard"
            label="Ledger Board"
            name="ledgerBoard"
            options={ledgerBoardOptions}
            value={calculatorValues.ledgerBoard}
          />
        </FormColumn>
      </FormRow>
      <AdditionalItemsForm />
      <FormRow>
        <Group wrapOnMobile>
          <Button disabled={!isFormValid} onClick={() => calculate(calculatorValues)}>
            Calculate Material Takeoff
          </Button>
          <ResetButton color="white" onClick={resetForm}>
            Reset
          </ResetButton>
        </Group>
      </FormRow>
    </CalculatorColumn>
  );
};

LumberDeckingForm.propTypes = {
  copy: PropTypes.shape({
    prehead: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    heading: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    bodyCopy: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }).isRequired,
};

export default LumberDeckingForm;
